import {queryOptions, type UseQueryOptions} from '@tanstack/react-query';
import {DEV_QUERY_KEY_MAP} from '../api/dev/constant';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {ApiStatusCodeVo as KarinaApiStatusCodeVo} from '../common/shared/api/karina/vo/enums/ApiStatusCodeVo.enum';
import {ApiStatusCodeVo as NicolasApiStatusCodeVo} from '../common/shared/api/nicolas/vo/enums/ApiStatusCodeVo.enum';
import {PagewebError} from '../errors/PagewebError.class';
import {PagewebErrorType} from '../errors/PagewebErrorType.enum';
import {CustomApolloError} from '../graphql/api/CustomApolloError';
import type {
  SampleErrorQuery,
  SampleErrorQueryVariables,
} from '../graphql/generated/types';

/**
 * Dev 도메인 Query Factory
 *
 * Adapter layer를 제거하고 Factory에서 직접 데이터 변환을 수행
 */
export const devFactory = {
  /**
   * 샘플 에러 테스트
   *
   * GraphQL 전용(실제 BFF 호출 없음): SSR/CSR에서 에러 전파 확인용
   */
  sampleError: (
    variables: SampleErrorQueryVariables,
    options?: Omit<
      UseQueryOptions<SampleErrorQuery, PagewebError, SampleErrorQuery>,
      'queryKey' | 'queryFn'
    >,
    _fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: DEV_QUERY_KEY_MAP.sampleError(variables),
      // 실제 BFF 호출 없이 에러 타입에 따라 다양한 에러 케이스를 시뮬레이션합니다.
      queryFn: async (): Promise<SampleErrorQuery> => {
        const errorType = variables.pagewebErrorType;
        if (errorType === PagewebErrorType.NicolasApiStatusError) {
          throw new CustomApolloError({
            isSerializedPagewebError: true,
            type: PagewebErrorType.NicolasApiStatusError,
            resultCode: NicolasApiStatusCodeVo.UnknownError,
            message: '니콜라스 실패',
          });
        } else if (errorType === PagewebErrorType.KarinaApiStatusError) {
          throw new CustomApolloError({
            isSerializedPagewebError: true,
            type: PagewebErrorType.KarinaApiStatusError,
            resultCode: KarinaApiStatusCodeVo.Fail,
            message: '카리나 실패',
          });
        } else if (errorType === PagewebErrorType.UnexpectedFetchError) {
          throw new CustomApolloError({
            isSerializedPagewebError: true,
            type: PagewebErrorType.UnexpectedFetchError,
            message: 'Some Fetch Error',
          });
        } else if (errorType === PagewebErrorType.ApolloError) {
          // Apollo's INTERNAL_SERVER_ERROR
          throw new Error('sample should be error');
        }

        return {sampleError: {sample: 'sample'}};
      },
      ...options,
    }),
} as const;
